<script lang="ts" setup>
import {
  Chart,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler,
  type ChartData,
  type ChartOptions,
} from 'chart.js';
import {Line, Doughnut, Bar} from 'vue-chartjs';
import {GET, POST} from '../modules/fetch.ts';
import {chartJsColors} from '../utils/color.ts';
import {computed, onBeforeUnmount, onMounted, ref, shallowRef, watch} from 'vue';

Chart.defaults.color = chartJsColors.text;
Chart.defaults.borderColor = chartJsColors.border;
Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler);

type TrendPoint = {
  label: string;
  value: number;
  value2?: number;
  value3?: number;
  value4?: number;
};

type DistributionSlice = {
  label: string;
  value: number;
};

type RankingItem = {
  label: string;
  value: number;
};

type WidgetID =
  | 'requirements'
  | 'issue_delivery_trend'
  | 'issue_type_distribution'
  | 'pipelines_today'
  | 'pipeline_trend'
  | 'recent_pipeline_duration'
  | 'bugs'
  | 'pr_merge_trend'
  | 'code_line_trend'
  | 'code_change_users_top'
  | 'code_review_users_top'
  | 'repository_storage_top'
  | 'pull_request_users_top'
  | 'active_repositories_top'
  | 'organization_activity_top'
  | 'organization_storage_top'
  | 'organization_pull_requests_top'
  | 'organization_code_reviews_top'
  | 'organization_code_changes_top'
  | 'team_activity_top'
  | 'team_storage_top'
  | 'team_pull_requests_top'
  | 'team_code_reviews_top'
  | 'team_code_changes_top';

type WidgetSize = '1x1' | '1x2' | '2x1' | '2x2' | '3x1';

type Payload = {
  meta: {
    generated_at: string;
    range_days: number;
    refresh_seconds: number;
    layout: Array<{id: WidgetID; size: WidgetSize}>;
  };
  summary: {
    requirements: {
      new_count: number;
      closed_count: number;
      daily_closed_avg: number;
      throughput_rate: number;
    };
    pipelines_today: {
      total: number;
      success: number;
      failure: number;
      waiting: number;
      running: number;
      other: number;
    };
    bugs: {
      new_count: number;
      closed_count: number;
      close_rate: number;
      avg_fix_hours: number;
    };
  };
  charts: {
    issue_delivery_trend: TrendPoint[];
    pipeline_trend: TrendPoint[];
    pr_merge_trend: TrendPoint[];
    recent_pipeline_duration: TrendPoint[];
    code_line_trend: TrendPoint[];
    issue_type_distribution: DistributionSlice[];
    code_change_users_top: RankingItem[];
    code_review_users_top: RankingItem[];
    repository_storage_top: TrendPoint[];
    pull_request_users_top: RankingItem[];
    active_repositories_top: RankingItem[];
    organization_activity_top: RankingItem[];
    organization_storage_top: TrendPoint[];
    organization_pull_requests_top: RankingItem[];
    organization_code_reviews_top: RankingItem[];
    organization_code_changes_top: RankingItem[];
    team_activity_top: RankingItem[];
    team_storage_top: TrendPoint[];
    team_pull_requests_top: RankingItem[];
    team_code_reviews_top: RankingItem[];
    team_code_changes_top: RankingItem[];
  };
};

const props = defineProps<{
  title: string;
  dataUrl: string;
  exportUrl: string;
  defaultRange: string;
  logoUrl: string;
  homeUrl: string;
  homeLabel: string;
  locale: Record<string, string>;
}>();

const payload = shallowRef<Payload | null>(null);
const errorText = ref('');
const exportErrorText = ref('');
const isExporting = ref(false);
const isLoading = ref(true);
const currentTime = ref(new Date());

let refreshTimer: number | null = null;
let clockTimer: number | null = null;

const currentTimeText = computed(() => formatDateTime(currentTime.value.toISOString()));
const orderedLayout = computed(() => payload.value?.meta.layout ?? []);

const gaugeEmptyColor = ref('rgba(148, 163, 184, 0.25)');
const gaugeAccentColor = ref('#14b8a6');

const issueTrendData = computed(() => buildDualLineChartData(
  payload.value?.charts.issue_delivery_trend ?? [],
  props.locale.createdSeries,
  props.locale.closedSeries,
  chartJsColors['issue-open'] ?? '#22c55e',
  chartJsColors['issue-closed'] ?? '#14b8a6',
));

const pipelineTrendData = computed(() => buildDualLineChartData(
  payload.value?.charts.pipeline_trend ?? [],
  props.locale.runSeries,
  props.locale.successSeries,
  '#60a5fa',
  '#14b8a6',
));

const prTrendData = computed(() => buildSingleLineChartData(
  payload.value?.charts.pr_merge_trend ?? [],
  props.locale.prMergeTrend,
  '#84cc16',
));

const durationTrendData = computed(() => buildSingleLineChartData(
  payload.value?.charts.recent_pipeline_duration ?? [],
  props.locale.durationSeries,
  '#f59e0b',
));

const codeLineTrendData = computed<ChartData<'line'>>(() => ({
  labels: (payload.value?.charts.code_line_trend ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.additionsSeries,
    data: (payload.value?.charts.code_line_trend ?? []).map((item) => item.value),
    fill: true,
    borderColor: '#22c55e',
    backgroundColor: '#22c55e22',
    pointBackgroundColor: '#22c55e',
    pointRadius: 2,
    pointHoverRadius: 5,
    tension: 0.35,
  }, {
    label: props.locale.deletionsSeries,
    data: (payload.value?.charts.code_line_trend ?? []).map((item) => item.value2 ?? 0),
    fill: true,
    borderColor: '#ef4444',
    backgroundColor: '#ef44441a',
    pointBackgroundColor: '#ef4444',
    pointRadius: 2,
    pointHoverRadius: 5,
    tension: 0.35,
  }, {
    label: props.locale.changedLinesSeries,
    data: (payload.value?.charts.code_line_trend ?? []).map((item) => item.value3 ?? 0),
    fill: true,
    borderColor: '#3b82f6',
    backgroundColor: '#3b82f61a',
    pointBackgroundColor: '#3b82f6',
    pointRadius: 2,
    pointHoverRadius: 5,
    tension: 0.35,
  }, {
    label: props.locale.changedFilesSeries,
    data: (payload.value?.charts.code_line_trend ?? []).map((item) => item.value4 ?? 0),
    fill: true,
    borderColor: '#8b5cf6',
    backgroundColor: '#8b5cf61a',
    pointBackgroundColor: '#8b5cf6',
    pointRadius: 2,
    pointHoverRadius: 5,
    tension: 0.35,
  }],
}));

const issueTypeDistributionData = computed<ChartData<'doughnut'>>(() => ({
  labels: (payload.value?.charts.issue_type_distribution ?? []).map((item) => item.label),
  datasets: [{
    data: (payload.value?.charts.issue_type_distribution ?? []).map((item) => item.value),
    backgroundColor: ['#14b8a6', '#84cc16', '#60a5fa', '#cbd5e1'],
    borderWidth: 0,
  }],
}));

const repoTopPalette = ['#0ea5e9', '#14b8a6', '#84cc16', '#f59e0b', '#a855f7', '#ef4444'];

const repoTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.active_repositories_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.activeRepositoriesTop,
    data: (payload.value?.charts.active_repositories_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.active_repositories_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const userTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.code_change_users_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.changedLines,
    data: (payload.value?.charts.code_change_users_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.code_change_users_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const reviewTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.code_review_users_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.reviews,
    data: (payload.value?.charts.code_review_users_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.code_review_users_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const repositoryStorageTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.repository_storage_top ?? []).map((item) => item.label),
  datasets: [{
    label: `${props.locale.repositorySize} (MiB)`,
    data: (payload.value?.charts.repository_storage_top ?? []).map((item) => bytesToMiB(item.value)),
    backgroundColor: '#3b82f6',
    borderRadius: 6,
    borderSkipped: false,
  }, {
    label: `${props.locale.lfsSize} (MiB)`,
    data: (payload.value?.charts.repository_storage_top ?? []).map((item) => bytesToMiB(item.value2 ?? 0)),
    backgroundColor: '#8b5cf6',
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const pullRequestUsersTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.pull_request_users_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.pullRequests,
    data: (payload.value?.charts.pull_request_users_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.pull_request_users_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const organizationStorageTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.organization_storage_top ?? []).map((item) => item.label),
  datasets: [{
    label: `${props.locale.repositorySize} (MiB)`,
    data: (payload.value?.charts.organization_storage_top ?? []).map((item) => bytesToMiB(item.value)),
    backgroundColor: '#0ea5e9',
    borderRadius: 6,
    borderSkipped: false,
  }, {
    label: `${props.locale.lfsSize} (MiB)`,
    data: (payload.value?.charts.organization_storage_top ?? []).map((item) => bytesToMiB(item.value2 ?? 0)),
    backgroundColor: '#8b5cf6',
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const organizationActivityTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.organization_activity_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.organizationActivityTop,
    data: (payload.value?.charts.organization_activity_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.organization_activity_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const organizationPullRequestsTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.organization_pull_requests_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.pullRequests,
    data: (payload.value?.charts.organization_pull_requests_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.organization_pull_requests_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const organizationCodeReviewsTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.organization_code_reviews_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.reviews,
    data: (payload.value?.charts.organization_code_reviews_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.organization_code_reviews_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const organizationCodeChangesTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.organization_code_changes_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.changedLines,
    data: (payload.value?.charts.organization_code_changes_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.organization_code_changes_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const teamStorageTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.team_storage_top ?? []).map((item) => item.label),
  datasets: [{
    label: `${props.locale.repositorySize} (MiB)`,
    data: (payload.value?.charts.team_storage_top ?? []).map((item) => bytesToMiB(item.value)),
    backgroundColor: '#0ea5e9',
    borderRadius: 6,
    borderSkipped: false,
  }, {
    label: `${props.locale.lfsSize} (MiB)`,
    data: (payload.value?.charts.team_storage_top ?? []).map((item) => bytesToMiB(item.value2 ?? 0)),
    backgroundColor: '#8b5cf6',
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const teamActivityTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.team_activity_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.teamActivityTop,
    data: (payload.value?.charts.team_activity_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.team_activity_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const teamPullRequestsTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.team_pull_requests_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.pullRequests,
    data: (payload.value?.charts.team_pull_requests_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.team_pull_requests_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const teamCodeReviewsTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.team_code_reviews_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.reviews,
    data: (payload.value?.charts.team_code_reviews_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.team_code_reviews_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const teamCodeChangesTopData = computed<ChartData<'bar'>>(() => ({
  labels: (payload.value?.charts.team_code_changes_top ?? []).map((item) => item.label),
  datasets: [{
    label: props.locale.changedLines,
    data: (payload.value?.charts.team_code_changes_top ?? []).map((item) => item.value),
    backgroundColor: (payload.value?.charts.team_code_changes_top ?? []).map((_, index) => repoTopPalette[index % repoTopPalette.length]),
    borderRadius: 6,
    borderSkipped: false,
  }],
}));

const widgetTooltip = ref({
  visible: false,
  text: '',
  x: 0,
  y: 0,
});

const lineOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {mode: 'index', intersect: false},
  scales: {
    x: {grid: {display: false}, ticks: {maxTicksLimit: 12}},
    y: {ticks: {maxTicksLimit: 5}, grid: {color: chartJsColors.border}},
  },
  plugins: {
    legend: {display: true, position: 'top', align: 'start'},
  },
};

const doughnutOptions: ChartOptions<'doughnut'> = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '70%',
  plugins: {
    legend: {position: 'right'},
  },
};

const barOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  scales: {
    x: {grid: {color: chartJsColors.border}},
    y: {grid: {display: false}, ticks: {font: {size: 11}}},
  },
  plugins: {
    legend: {display: false},
  },
};

const dualBarOptions: ChartOptions<'bar'> = {
  ...barOptions,
  plugins: {
    legend: {display: true, position: 'top', align: 'start'},
  },
};

onMounted(async () => {
  const secondary = window.getComputedStyle(document.documentElement).getPropertyValue('--color-secondary').trim();
  if (secondary) {
    gaugeEmptyColor.value = secondary.startsWith('#') ? `${secondary}40` : secondary;
  }
  clockTimer = window.setInterval(() => {
    currentTime.value = new Date();
  }, 1000);
  await fetchPayload();
});

onBeforeUnmount(() => {
  if (refreshTimer !== null) window.clearInterval(refreshTimer);
  if (clockTimer !== null) window.clearInterval(clockTimer);
});

watch(payload, (value) => {
  if (!value) return;
  if (refreshTimer !== null) window.clearInterval(refreshTimer);
  refreshTimer = window.setInterval(() => {
    void fetchPayload(false);
  }, value.meta.refresh_seconds * 1000);
});

async function fetchPayload(showLoading = true) {
  if (showLoading) isLoading.value = true;
  try {
    const response = await GET(`${props.dataUrl}?range=${encodeURIComponent(props.defaultRange)}`);
    if (!response.ok) {
      throw new Error(response.statusText || 'Request failed');
    }
    payload.value = await response.json() as Payload;
    errorText.value = '';
  } catch (err) {
    errorText.value = err instanceof Error ? err.message : String(err);
  } finally {
    isLoading.value = false;
  }
}

async function exportExcel() {
  if (!payload.value || !props.exportUrl || isExporting.value) return;

  isExporting.value = true;
  exportErrorText.value = '';

  try {
    const response = await POST(props.exportUrl, {data: {payload: payload.value}});
    if (!response.ok) {
      let message = '';
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json() as {errorMessage?: string};
        message = data.errorMessage?.trim() || '';
      } else {
        message = (await response.text()).trim();
      }
      throw new Error(message || props.locale.exportFailed || response.statusText || 'Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = parseDownloadFilename(response.headers.get('content-disposition')) ?? 'devops_dashboard.xlsx';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
  } catch (err) {
    exportErrorText.value = err instanceof Error ? err.message : String(err);
  } finally {
    isExporting.value = false;
  }
}

function buildSingleLineChartData(points: TrendPoint[], label: string, color: string): ChartData<'line'> {
  return {
    labels: points.map((item) => item.label),
    datasets: [{
      label,
      data: points.map((item) => item.value),
      fill: true,
      borderColor: color,
      backgroundColor: `${color}2a`,
      pointBackgroundColor: color,
      pointRadius: 2,
      pointHoverRadius: 5,
      tension: 0.35,
    }],
  };
}

function buildDualLineChartData(points: TrendPoint[], label1: string, label2: string, color1: string, color2: string): ChartData<'line'> {
  return {
    labels: points.map((item) => item.label),
    datasets: [{
      label: label1,
      data: points.map((item) => item.value),
      fill: true,
      borderColor: color1,
      backgroundColor: `${color1}2a`,
      pointBackgroundColor: color1,
      pointRadius: 2,
      pointHoverRadius: 5,
      tension: 0.35,
    }, {
      label: label2,
      data: points.map((item) => item.value2 ?? 0),
      fill: true,
      borderColor: color2,
      backgroundColor: `${color2}1a`,
      pointBackgroundColor: color2,
      pointRadius: 2,
      pointHoverRadius: 5,
      tension: 0.35,
    }],
  };
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(new Date(value));
}

function formatNumber(value: number, fractionDigits = 0) {
  return new Intl.NumberFormat(undefined, {maximumFractionDigits: fractionDigits, minimumFractionDigits: fractionDigits}).format(value);
}

function formatPercent(value: number) {
  return `${formatNumber(value * 100, 2)}%`;
}

function bytesToMiB(value: number) {
  return value / (1024 * 1024);
}

function gaugeStyle(value: number) {
  const percent = Math.max(0, Math.min(100, value * 100));
  return {background: `conic-gradient(${gaugeAccentColor.value} ${percent}%, ${gaugeEmptyColor.value} ${percent}% 100%)`};
}

function parseDownloadFilename(contentDisposition: string | null) {
  if (!contentDisposition) return null;
  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) return decodeURIComponent(encodedMatch[1]);
  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];
  const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
}

function widgetDescription(widgetID: WidgetID) {
  switch (widgetID) {
    case 'requirements': return props.locale.widgetDescRequirements;
    case 'issue_delivery_trend': return props.locale.widgetDescIssueDeliveryTrend;
    case 'issue_type_distribution': return props.locale.widgetDescIssueTypeDistribution;
    case 'pipelines_today': return props.locale.widgetDescPipelinesToday;
    case 'pipeline_trend': return props.locale.widgetDescPipelineTrend;
    case 'recent_pipeline_duration': return props.locale.widgetDescRecentPipelineDuration;
    case 'bugs': return props.locale.widgetDescBugs;
    case 'pr_merge_trend': return props.locale.widgetDescPRMergeTrend;
    case 'code_line_trend': return props.locale.widgetDescCodeLineTrend;
    case 'code_change_users_top': return props.locale.widgetDescCodeChangeUsersTop;
    case 'code_review_users_top': return props.locale.widgetDescCodeReviewUsersTop;
    case 'repository_storage_top': return props.locale.widgetDescRepositoryStorageTop;
    case 'pull_request_users_top': return props.locale.widgetDescPullRequestUsersTop;
    case 'active_repositories_top': return props.locale.widgetDescActiveRepositoriesTop;
    case 'organization_activity_top': return props.locale.widgetDescOrganizationActivityTop;
    case 'organization_storage_top': return props.locale.widgetDescOrganizationStorageTop;
    case 'organization_pull_requests_top': return props.locale.widgetDescOrganizationPullRequestsTop;
    case 'organization_code_reviews_top': return props.locale.widgetDescOrganizationCodeReviewsTop;
    case 'organization_code_changes_top': return props.locale.widgetDescOrganizationCodeChangesTop;
    case 'team_storage_top': return props.locale.widgetDescTeamStorageTop;
    case 'team_activity_top': return props.locale.widgetDescTeamActivityTop;
    case 'team_pull_requests_top': return props.locale.widgetDescTeamPullRequestsTop;
    case 'team_code_reviews_top': return props.locale.widgetDescTeamCodeReviewsTop;
    case 'team_code_changes_top': return props.locale.widgetDescTeamCodeChangesTop;
  }
}

function widgetTooltipHandlers(widgetID: WidgetID) {
  return {
    mouseenter: (event: MouseEvent) => showWidgetTooltip(event, widgetID),
    mousemove: (event: MouseEvent) => moveWidgetTooltip(event),
    mouseleave: () => hideWidgetTooltip(),
    focus: (event: FocusEvent) => showWidgetTooltip(event, widgetID),
    blur: () => hideWidgetTooltip(),
  };
}

function showWidgetTooltip(event: MouseEvent | FocusEvent, widgetID: WidgetID) {
  const text = widgetDescription(widgetID);
  if (!text) return;
  widgetTooltip.value.visible = true;
  widgetTooltip.value.text = text;
  positionWidgetTooltip(event);
}

function moveWidgetTooltip(event: MouseEvent) {
  if (!widgetTooltip.value.visible) return;
  positionWidgetTooltip(event);
}

function hideWidgetTooltip() {
  widgetTooltip.value.visible = false;
}

function positionWidgetTooltip(event: MouseEvent | FocusEvent) {
  const offset = 16;
  if (event instanceof MouseEvent) {
    widgetTooltip.value.x = Math.min(event.clientX + offset, window.innerWidth - 320);
    widgetTooltip.value.y = Math.min(event.clientY + offset, window.innerHeight - 120);
    return;
  }
  const target = event.currentTarget as HTMLElement | null;
  if (!target) return;
  const rect = target.getBoundingClientRect();
  widgetTooltip.value.x = Math.min(rect.left, window.innerWidth - 320);
  widgetTooltip.value.y = Math.min(rect.bottom + 8, window.innerHeight - 120);
}

</script>

<template>
  <div class="devops-dashboard-shell">
    <header class="devops-dashboard-header">
      <div class="devops-dashboard-branding">
        <img class="devops-dashboard-logo" :src="props.logoUrl" alt="Gitea">
        <div>
          <div class="devops-dashboard-title-row">
            <h1>{{ title }}</h1>
            <a
              v-if="props.homeUrl"
              class="devops-dashboard-home-link"
              :href="props.homeUrl"
              :aria-label="props.homeLabel || 'Back to Gitea Home'"
              :title="props.homeLabel || 'Back to Gitea Home'"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                <path d="M6.78 2.97a.75.75 0 0 1 0 1.06L3.81 7h8.44a.75.75 0 0 1 0 1.5H3.81l2.97 2.97a.75.75 0 1 1-1.06 1.06L1.47 8.28a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
      <div class="devops-dashboard-toolbar">
        <div class="devops-dashboard-meta">
          <span>{{ locale.currentTime }}: {{ currentTimeText }}</span>
          <span v-if="payload">{{ locale.range }}: {{ payload.meta.range_days }}d / {{ locale.refreshSeconds }}: {{ payload.meta.refresh_seconds }}s</span>
        </div>
        <button class="ui button primary devops-dashboard-export" type="button" :disabled="!payload || isExporting" @click="exportExcel">
          {{ isExporting ? locale.exportingExcel : locale.exportExcel }}
        </button>
      </div>
    </header>

    <div v-if="isLoading && !payload" class="ui segment dashboard-state">{{ locale.loading }}</div>
    <div v-else-if="errorText && !payload" class="ui negative message dashboard-state">{{ locale.loadFailed }}: {{ errorText }}</div>

    <template v-if="payload">
      <div v-if="errorText" class="ui warning message dashboard-warning">{{ locale.loadFailed }}: {{ errorText }}</div>
      <div v-if="exportErrorText" class="ui warning message dashboard-warning">{{ locale.exportFailed }}: {{ exportErrorText }}</div>

      <section class="dashboard-grid dashboard-card-grid">
        <template v-for="item in orderedLayout" :key="item.id">
          <article v-if="item.id === 'requirements'" class="dashboard-card dashboard-card-gauge" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.requirements }}</div>
            <div class="dashboard-gauge-layout">
              <div class="dashboard-gauge" :style="gaugeStyle(payload.summary.requirements.throughput_rate)">
                <div class="dashboard-gauge-inner">
                  <strong>{{ formatPercent(payload.summary.requirements.throughput_rate) }}</strong>
                  <span>{{ locale.throughputRate }}</span>
                </div>
              </div>
              <dl class="dashboard-metrics-list">
                <div><dt>{{ locale.closedCount }}</dt><dd>{{ formatNumber(payload.summary.requirements.closed_count) }}</dd></div>
                <div><dt>{{ locale.newCount }}</dt><dd>{{ formatNumber(payload.summary.requirements.new_count) }}</dd></div>
                <div><dt>{{ locale.dailyClosedAvg }}</dt><dd>{{ formatNumber(payload.summary.requirements.daily_closed_avg, 2) }}</dd></div>
              </dl>
            </div>
          </article>

          <article v-else-if="item.id === 'issue_delivery_trend'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.issueDeliveryTrend }}</div>
            <div class="chart-box"><Line :data="issueTrendData" :options="lineOptions"/></div>
          </article>

          <article v-else-if="item.id === 'issue_type_distribution'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.issueTypeDistribution }}</div>
            <div class="chart-box"><Doughnut :data="issueTypeDistributionData" :options="doughnutOptions"/></div>
          </article>

          <article v-else-if="item.id === 'pipelines_today'" class="dashboard-card dashboard-card-pipeline" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.pipelinesToday }}</div>
            <div class="dashboard-kpi-total">
              <span>{{ locale.totalRuns }}</span>
              <strong>{{ formatNumber(payload.summary.pipelines_today.total) }}</strong>
            </div>
            <div class="dashboard-status-grid">
              <div class="status-chip status-success"><span>{{ locale.statusSuccess }}</span><strong>{{ formatNumber(payload.summary.pipelines_today.success) }}</strong></div>
              <div class="status-chip status-failure"><span>{{ locale.statusFailure }}</span><strong>{{ formatNumber(payload.summary.pipelines_today.failure) }}</strong></div>
              <div class="status-chip status-waiting"><span>{{ locale.statusWaiting }}</span><strong>{{ formatNumber(payload.summary.pipelines_today.waiting) }}</strong></div>
              <div class="status-chip status-running"><span>{{ locale.statusRunning }}</span><strong>{{ formatNumber(payload.summary.pipelines_today.running) }}</strong></div>
              <div class="status-chip status-other"><span>{{ locale.statusOther }}</span><strong>{{ formatNumber(payload.summary.pipelines_today.other) }}</strong></div>
            </div>
          </article>

          <article v-else-if="item.id === 'pipeline_trend'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.pipelineTrend }}</div>
            <div class="chart-box"><Line :data="pipelineTrendData" :options="lineOptions"/></div>
          </article>

          <article v-else-if="item.id === 'recent_pipeline_duration'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.recentPipelineDuration }}</div>
            <div class="chart-box"><Line :data="durationTrendData" :options="lineOptions"/></div>
          </article>

          <article v-else-if="item.id === 'code_line_trend'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.codeLineTrend }}</div>
            <div class="chart-box"><Line :data="codeLineTrendData" :options="lineOptions"/></div>
          </article>

          <article v-else-if="item.id === 'bugs'" class="dashboard-card dashboard-card-gauge" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.bugs }}</div>
            <div class="dashboard-gauge-layout">
              <div class="dashboard-gauge" :style="gaugeStyle(payload.summary.bugs.close_rate)">
                <div class="dashboard-gauge-inner">
                  <strong>{{ formatPercent(payload.summary.bugs.close_rate) }}</strong>
                  <span>{{ locale.closeRate }}</span>
                </div>
              </div>
              <dl class="dashboard-metrics-list">
                <div><dt>{{ locale.closedCount }}</dt><dd>{{ formatNumber(payload.summary.bugs.closed_count) }}</dd></div>
                <div><dt>{{ locale.newCount }}</dt><dd>{{ formatNumber(payload.summary.bugs.new_count) }}</dd></div>
                <div><dt>{{ locale.avgFixHours }}</dt><dd>{{ formatNumber(payload.summary.bugs.avg_fix_hours, 2) }}</dd></div>
              </dl>
            </div>
          </article>

          <article v-else-if="item.id === 'pr_merge_trend'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.prMergeTrend }}</div>
            <div class="chart-box"><Line :data="prTrendData" :options="lineOptions"/></div>
          </article>

          <article v-else-if="item.id === 'code_change_users_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.codeChangeUsersTop }}</div>
            <div class="chart-box"><Bar :data="userTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'code_review_users_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.codeReviewUsersTop }}</div>
            <div class="chart-box"><Bar :data="reviewTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'repository_storage_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.repositoryStorageTop }}</div>
            <div class="chart-box"><Bar :data="repositoryStorageTopData" :options="dualBarOptions"/></div>
          </article>

          <article v-else-if="item.id === 'pull_request_users_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.pullRequestUsersTop }}</div>
            <div class="chart-box"><Bar :data="pullRequestUsersTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'active_repositories_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.activeRepositoriesTop }}</div>
            <div class="chart-box"><Bar :data="repoTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'organization_activity_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.organizationActivityTop }}</div>
            <div class="chart-box"><Bar :data="organizationActivityTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'organization_storage_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.organizationStorageTop }}</div>
            <div class="chart-box"><Bar :data="organizationStorageTopData" :options="dualBarOptions"/></div>
          </article>

          <article v-else-if="item.id === 'organization_pull_requests_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.organizationPullRequestsTop }}</div>
            <div class="chart-box"><Bar :data="organizationPullRequestsTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'organization_code_reviews_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.organizationCodeReviewsTop }}</div>
            <div class="chart-box"><Bar :data="organizationCodeReviewsTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'organization_code_changes_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.organizationCodeChangesTop }}</div>
            <div class="chart-box"><Bar :data="organizationCodeChangesTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'team_storage_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.teamStorageTop }}</div>
            <div class="chart-box"><Bar :data="teamStorageTopData" :options="dualBarOptions"/></div>
          </article>

          <article v-else-if="item.id === 'team_activity_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.teamActivityTop }}</div>
            <div class="chart-box"><Bar :data="teamActivityTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'team_pull_requests_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.teamPullRequestsTop }}</div>
            <div class="chart-box"><Bar :data="teamPullRequestsTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'team_code_reviews_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.teamCodeReviewsTop }}</div>
            <div class="chart-box"><Bar :data="teamCodeReviewsTopData" :options="barOptions"/></div>
          </article>

          <article v-else-if="item.id === 'team_code_changes_top'" class="dashboard-card" :data-widget-id="item.id" :data-widget-size="item.size">
            <div class="dashboard-card-title" v-on="widgetTooltipHandlers(item.id)">{{ locale.teamCodeChangesTop }}</div>
            <div class="chart-box"><Bar :data="teamCodeChangesTopData" :options="barOptions"/></div>
          </article>
        </template>
      </section>
    </template>
    <div v-if="widgetTooltip.visible" class="dashboard-widget-tooltip" :style="{left: `${widgetTooltip.x}px`, top: `${widgetTooltip.y}px`}">
      <span class="dashboard-widget-tooltip-arrow" aria-hidden="true"/>
      {{ widgetTooltip.text }}
    </div>
  </div>
</template>
